/**
 * PendingKeypairsPanel — accountants whose browser-derived O_pk is
 * waiting for `dovach-op sign`.
 *
 * Each row gives the operator everything they need to mint a v=2 cert
 * with the operator-cli: account_id, office_pk, plus a one-click
 * "copy CLI command" button so they don't typo IDs into a terminal.
 */

import {
  Alert,
  Button,
  CopyButton,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useState } from "react";

import { ApiError, api, type PendingKeypair } from "../lib/api";

export function PendingKeypairsPanel() {
  const [items, setItems] = useState<PendingKeypair[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const resp = await api.pendingKeypairs();
      setItems(resp.keypairs);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "fetch failed");
    }
  }, []);

  useEffect(() => {
    refresh();
    const i = window.setInterval(refresh, 15000);
    return () => window.clearInterval(i);
  }, [refresh]);

  if (error) return <Alert color="red">{error}</Alert>;
  if (items === null) return <Loader size="sm" />;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Pending keypairs</Title>
        <Button variant="light" onClick={refresh}>
          Refresh
        </Button>
      </Group>
      <Text c="dimmed">
        Each row is an accountant who has enrolled a browser-side keypair
        and is waiting for <code>dovach-op sign</code>. Copy the CLI
        command and run it on your signing laptop.
      </Text>

      {items.length === 0 ? (
        <Paper p="xl" withBorder>
          <Text c="dimmed">No pending keypairs.</Text>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Account</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Office pk</Table.Th>
                <Table.Th>Enrolled</Table.Th>
                <Table.Th>CLI</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((p) => {
                const cli = `dovach-op sign --account-id ${p.account_id.replace(/-/g, "")} --office-pk ${p.office_pk}`;
                return (
                  <Table.Tr key={p.account_id}>
                    <Table.Td style={{ fontFamily: "monospace", fontSize: 12 }}>
                      {p.account_id.slice(0, 12)}…
                    </Table.Td>
                    <Table.Td>{p.email}</Table.Td>
                    <Table.Td style={{ fontFamily: "monospace", fontSize: 12 }}>
                      {p.office_pk.slice(0, 16)}…
                    </Table.Td>
                    <Table.Td>
                      {new Date(p.enrolled_at).toLocaleString()}
                    </Table.Td>
                    <Table.Td>
                      <CopyButton value={cli}>
                        {({ copied, copy }) => (
                          <Button
                            size="xs"
                            variant={copied ? "filled" : "light"}
                            color={copied ? "green" : "blue"}
                            onClick={copy}
                          >
                            {copied ? "Copied" : "Copy command"}
                          </Button>
                        )}
                      </CopyButton>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
